import { Component, computed, effect, EventEmitter, inject, input, OnInit, Output, signal } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { BehaviorSubject, catchError, combineLatest, distinctUntilChanged, map, take, tap } from 'rxjs'
import { AuthManager } from 'src/app/shared/auth/AuthManager'
import { Input } from '@angular/core'
import { AppPractice, PracticesClient } from 'src/app/shared/services/ppv.api'

@Component({
   selector: 'vc-practice-select',
   templateUrl: './practice-select.component.html',
   styleUrls: ['./practice-select.component.scss'],
   standalone: false,
})
export class PracticeSelectComponent implements OnInit {
   @Input() urlBase: string
   onlySourcePractices = input<boolean>(false)
   showPracticesUserDoesNotHave = input<boolean>(false) //this is for reversing the selector so it shows practices a user does not have access to
   userPracticesOverride = input<number[]>([]) //this is for overriding the list of user practices. used in conjuction with showPracticesUserDoesNotHave to show practices a selected user may not have
   autoselect = input<boolean>(false)
   hidePrompt = input<boolean>(false)
   placeholderText = input<string>('Select a hospital')
   practiceSKey = input<number>(undefined)
   @Output() onSelected = new EventEmitter<number>()

   practicesLookup = signal<{
      [practiceSKey: number]: AppPractice
   }>(undefined)

   //this gets populated with the user's available practices
   practices: AppPractice[] = []

   //find the selected practice based on the url param
   selectedPracticeSKey = signal<number>(undefined)
   selectedPractice = computed(() => (this.practicesLookup() ? this.practicesLookup()[this.selectedPracticeSKey()] : undefined))

   authManager = inject(AuthManager)
   practicesController = inject(PracticesClient)

   //prettier-ignore
   constructor(
      private route: ActivatedRoute,
      private router: Router,
    ) {
      // Effect to update practiceSKeyNumber when practiceSKey changes
      effect(() => {
         const practiceSKey = this.practiceSKey()
         if (practiceSKey) {
            this.selectedPracticeSKey.set(this.practiceSKey())
         }
      })

      effect(() => {
         if (!this.practicesLookup())
            return;

         this.onPracticeSelected(this.selectedPracticeSKey())
      })

      effect(() => {
         //return if this is just showing regular user practices
         if (!this.showPracticesUserDoesNotHave()) {
            return
         } 

         //otherwise get the practices to exclude from the override list
         const overrideIds = this.userPracticesOverride()
         const excludeIds = new Set(overrideIds)

         //get all practices and filter out the ones to exclude
         this.practicesController.getAppPractices().pipe(take(1)).subscribe((allPractices) => {
            const filtered = allPractices.filter((p) => !excludeIds.has(p.id))
            this.filterAndUpdatePracticesDropdown(filtered)
         })
      })
   }

   ngOnInit(): void {
      this.route.paramMap
         .pipe(
            map((params) => Number(params.get('practiceSKey'))),
            distinctUntilChanged(),
            tap((practiceSKey) => {
               if (practiceSKey) this.selectedPracticeSKey.set(practiceSKey)
            })
         )
         .subscribe()

      if (!this.showPracticesUserDoesNotHave()) {
         //show the practices for the user
         this.authManager
            .getCurrentUser()
            .pipe(
               take(1),
               tap((user) => {
                  this.filterAndUpdatePracticesDropdown(user.practices)
               })
            )
            .subscribe()
      }
   }

   filterAndUpdatePracticesDropdown(practices: AppPractice[]) {
      this.practices = practices.filter((p) => !this.onlySourcePractices() || !p.doesNotSync)

      this.practices = this.practices.sort((a, b) => {
         const aClose = a.nameId?.toUpperCase().startsWith('(CLOSE')
         const bClose = b.nameId?.toUpperCase().startsWith('(CLOSE')
         if (aClose && !bClose) return 1
         if (!aClose && bClose) return -1
         // If both are the same regarding 'CLOSE', sort by name
         return a.nameId.localeCompare(b.nameId)
      })

      this.practices = this.practices.map((p) => ({
         ...p,
         searchableText: `${p.nameId} (${p.id})`,
      }))

      const newLookup = {}
      this.practices.forEach((prac) => {
         newLookup[prac.id] = prac
      })
      this.practicesLookup.set(newLookup)
      if (this.practices.length == 1 || this.autoselect()) {
         this.selectedPracticeSKey.set(this.practices[0]?.id)
      }
   }

   onDropdownSelected(practiceId: number) {
      this.selectedPracticeSKey.set(practiceId)
   }

   onPracticeSelected(practiceSKey) {
      let practice: AppPractice

      practice = this.practices.find((p) => p.id === practiceSKey)

      console.log(`${practice?.nameId} (${practice?.id}) - ${practiceSKey}`)

      if (!this.urlBase) {
         this.onSelected.emit(practice?.id)
         return
      }

      if (!!practice) {
         // Only route if necessary
         if (Number(this.route.snapshot.paramMap.get('practiceSKey')) == practice?.id) {
            return
         }

         //reload the parent component by navigating to '/' first
         this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
            this.router.navigate([this.urlBase, practice?.id])
         })
      } else {
         this.router.navigate([this.urlBase])
      }
   }
}
